/**
 * ARIA Admin Settings Page
 * Version: 1.1.0
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  Bell,
  Calendar,
  MapPin,
  Save,
  CheckCircle,
  Database,
  Users as UsersIcon,
  Plus,
  KeyRound,
  UserX,
  UserCheck,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAppSelector } from '../../store';
import { AddUserModal } from '../../components/admin/AddUserModal';
import { ResetPasswordModal } from '../../components/admin/ResetPasswordModal';

const ADMIN_SETTINGS_QUERY = gql`
  query AdminSettings {
    adminSettings {
      scheduling {
        defaultJobDuration
        bufferBetweenJobs
        maxJobsPerDay
        workingHoursStart
        workingHoursEnd
      }
      notifications {
        emailEnabled
        pushEnabled
        smsEnabled
        reminderHoursBefore
        testEmailOverride
      }
      integrations {
        graphApiEnabled
        graphApiLastSync
        aiAssistantEnabled
      }
      zones {
        id
        name
        description
        active
      }
    }
  }
`;

const USERS_QUERY = gql`
  query Users {
    users {
      id
      email
      name
      role
      isActive
      lastLoginAt
      createdAt
    }
  }
`;

const SET_USER_ACTIVE_MUTATION = gql`
  mutation SetUserActive($id: ID!, $isActive: Boolean!) {
    setUserActive(id: $id, isActive: $isActive) {
      id
      isActive
    }
  }
`;

const UPDATE_SETTINGS_MUTATION = gql`
  mutation UpdateAdminSettings($input: AdminSettingsInput!) {
    updateAdminSettings(input: $input) {
      scheduling {
        defaultJobDuration
        bufferBetweenJobs
        maxJobsPerDay
        workingHoursStart
        workingHoursEnd
      }
      notifications {
        emailEnabled
        pushEnabled
        smsEnabled
        reminderHoursBefore
        testEmailOverride
      }
    }
  }
`;

interface SchedulingSettings {
  defaultJobDuration: number;
  bufferBetweenJobs: number;
  maxJobsPerDay: number;
  workingHoursStart: string;
  workingHoursEnd: string;
}

interface NotificationSettings {
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
  reminderHoursBefore: number;
  testEmailOverride: string;
}

interface IntegrationStatus {
  graphApiEnabled: boolean;
  graphApiLastSync: string | null;
  aiAssistantEnabled: boolean;
}

interface Zone {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface ManagedUser {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export default function AdminSettings() {
  const { data, loading } = useQuery(ADMIN_SETTINGS_QUERY);
  const [updateSettings, { loading: saving }] = useMutation(UPDATE_SETTINGS_MUTATION);

  // User management (ARIA auth Option B) — admin-only, hidden from other roles.
  const currentUser = useAppSelector((state) => state.user.user);
  const isAdmin = !!currentUser?.roles.includes('ADMIN');
  const { data: usersData, loading: usersLoading, refetch: refetchUsers } = useQuery(USERS_QUERY, {
    skip: !isAdmin,
  });
  const [setUserActive] = useMutation(SET_USER_ACTIVE_MUTATION);
  const users = (usersData?.users ?? []) as ManagedUser[];
  const [showAddUser, setShowAddUser] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<ManagedUser | null>(null);

  const handleToggleActive = async (user: ManagedUser) => {
    try {
      await setUserActive({ variables: { id: user.id, isActive: !user.isActive } });
      refetchUsers();
    } catch (err) {
      console.error('Failed to update user status:', err);
    }
  };

  const [scheduling, setScheduling] = useState<SchedulingSettings>({
    defaultJobDuration: 60,
    bufferBetweenJobs: 15,
    maxJobsPerDay: 8,
    workingHoursStart: '08:00',
    workingHoursEnd: '17:00',
  });

  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailEnabled: true,
    pushEnabled: true,
    smsEnabled: false,
    reminderHoursBefore: 24,
    testEmailOverride: '',
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Seed state from server once loaded
  useEffect(() => {
    if (data?.adminSettings) {
      const s = data.adminSettings.scheduling;
      const n = data.adminSettings.notifications;
      // Pick known fields rather than spreading the query result directly —
      // Apollo includes __typename on every object, which GraphQL input
      // types reject when this state is fed back into updateAdminSettings.
      if (s) {
        setScheduling({
          defaultJobDuration: s.defaultJobDuration,
          bufferBetweenJobs: s.bufferBetweenJobs,
          maxJobsPerDay: s.maxJobsPerDay,
          workingHoursStart: s.workingHoursStart,
          workingHoursEnd: s.workingHoursEnd,
        });
      }
      if (n) {
        setNotifications({
          emailEnabled: n.emailEnabled,
          pushEnabled: n.pushEnabled,
          smsEnabled: n.smsEnabled,
          reminderHoursBefore: n.reminderHoursBefore,
          testEmailOverride: n.testEmailOverride ?? '',
        });
      }
    }
  }, [data]);

  const integrations = (data?.adminSettings?.integrations ?? {}) as IntegrationStatus;
  const zones = (data?.adminSettings?.zones ?? []) as Zone[];

  const handleSchedulingChange = (key: keyof SchedulingSettings, value: number | string) => {
    setScheduling((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleNotificationChange = (key: keyof NotificationSettings, value: boolean | number | string) => {
    setNotifications((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updateSettings({
        variables: {
          input: {
            scheduling,
            notifications: {
              ...notifications,
              testEmailOverride: notifications.testEmailOverride.trim() || null,
            },
          },
        },
      });
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12 text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
        <p className="text-gray-600">System configuration and integrations</p>
      </header>

      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          Settings saved successfully!
        </div>
      )}

      <div className="space-y-6">
        {/* Users — admin-only */}
        {isAdmin && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <UsersIcon className="w-5 h-5" />
                Users
              </h2>
              <button onClick={() => setShowAddUser(true)} className="btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add User
              </button>
            </div>

            {usersLoading ? (
              <p className="text-gray-500">Loading users…</p>
            ) : users.length === 0 ? (
              <p className="text-gray-500">No users yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4 font-medium">Name</th>
                      <th className="py-2 pr-4 font-medium">Email</th>
                      <th className="py-2 pr-4 font-medium">Role</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Last Login</th>
                      <th className="py-2 pr-4 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-gray-100 last:border-0">
                        <td className="py-2 pr-4 text-gray-900">{user.name}</td>
                        <td className="py-2 pr-4 text-gray-600">{user.email}</td>
                        <td className="py-2 pr-4">
                          <span className={clsx('badge', user.role === 'ADMIN' ? 'badge-purple' : 'badge-gray')}>
                            {user.role}
                          </span>
                        </td>
                        <td className="py-2 pr-4">
                          <span className={clsx('badge', user.isActive ? 'badge-green' : 'badge-gray')}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-gray-500">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
                        </td>
                        <td className="py-2 pr-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setResetPasswordUser(user)}
                              title="Reset password"
                              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                            >
                              <KeyRound className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleToggleActive(user)}
                              disabled={user.id === currentUser?.id}
                              title={
                                user.id === currentUser?.id
                                  ? "You can't deactivate your own account"
                                  : user.isActive
                                    ? 'Deactivate'
                                    : 'Activate'
                              }
                              className={clsx(
                                'p-1.5 rounded-lg hover:bg-gray-100',
                                user.id === currentUser?.id
                                  ? 'text-gray-200 cursor-not-allowed hover:bg-transparent'
                                  : 'text-gray-400 hover:text-gray-600'
                              )}
                            >
                              {user.isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Scheduling */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Scheduling
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Job Duration (minutes)
              </label>
              <input
                type="number"
                min={15}
                max={480}
                step={15}
                value={scheduling.defaultJobDuration}
                onChange={(e) =>
                  handleSchedulingChange('defaultJobDuration', parseInt(e.target.value) || 60)
                }
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buffer Between Jobs (minutes)
              </label>
              <input
                type="number"
                min={0}
                max={60}
                step={5}
                value={scheduling.bufferBetweenJobs}
                onChange={(e) =>
                  handleSchedulingChange('bufferBetweenJobs', parseInt(e.target.value) || 15)
                }
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Jobs Per Technician/Day
              </label>
              <input
                type="number"
                min={1}
                max={20}
                value={scheduling.maxJobsPerDay}
                onChange={(e) =>
                  handleSchedulingChange('maxJobsPerDay', parseInt(e.target.value) || 8)
                }
                className="input"
              />
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Working Hours Start
                </label>
                <input
                  type="time"
                  value={scheduling.workingHoursStart}
                  onChange={(e) => handleSchedulingChange('workingHoursStart', e.target.value)}
                  className="input"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Working Hours End
                </label>
                <input
                  type="time"
                  value={scheduling.workingHoursEnd}
                  onChange={(e) => handleSchedulingChange('workingHoursEnd', e.target.value)}
                  className="input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifications
          </h2>

          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.emailEnabled}
                onChange={() =>
                  handleNotificationChange('emailEnabled', !notifications.emailEnabled)
                }
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">Email notifications</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.pushEnabled}
                onChange={() => handleNotificationChange('pushEnabled', !notifications.pushEnabled)}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">Push notifications</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.smsEnabled}
                onChange={() => handleNotificationChange('smsEnabled', !notifications.smsEnabled)}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">SMS notifications</span>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Appointment Reminder (hours before)
              </label>
              <input
                type="number"
                min={1}
                max={72}
                value={notifications.reminderHoursBefore}
                onChange={(e) =>
                  handleNotificationChange('reminderHoursBefore', parseInt(e.target.value) || 24)
                }
                className="input w-32"
              />
            </div>

            {/* Test email override */}
            <div className="pt-2 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Test Email Override
              </label>
              <input
                type="email"
                placeholder="e.g. joe@example.com — leave blank to send to real recipients"
                value={notifications.testEmailOverride}
                onChange={(e) => handleNotificationChange('testEmailOverride', e.target.value)}
                className="input max-w-md"
              />
              <p className="mt-1 text-xs text-gray-500">
                When set, all outgoing emails are redirected to this address instead of the actual recipient.
              </p>
            </div>
          </div>
        </div>

        {/* Integrations */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5" />
            Integrations
          </h2>

          <div className="space-y-4">
            {/* Microsoft Graph API */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">Microsoft Graph API</h4>
                <p className="text-sm text-gray-500">
                  {integrations.graphApiEnabled
                    ? integrations.graphApiLastSync
                      ? `Last sync: ${new Date(integrations.graphApiLastSync).toLocaleString()}`
                      : 'Calendar and email integration'
                    : 'Configure AZURE_TENANT_ID, AZURE_CLIENT_ID, AZURE_CLIENT_SECRET'}
                </p>
              </div>
              <span
                className={clsx(
                  'badge',
                  integrations.graphApiEnabled
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                )}
              >
                {integrations.graphApiEnabled ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {/* AI Assistant */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">AI Assistant</h4>
                <p className="text-sm text-gray-500">Azure OpenAI GPT-4o</p>
              </div>
              <span
                className={clsx(
                  'badge',
                  integrations.aiAssistantEnabled
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                )}
              >
                {integrations.aiAssistantEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>

        {/* Service Zones */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Service Zones
          </h2>

          {zones.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {zones.map((zone) => (
                <div
                  key={zone.id}
                  className={clsx(
                    'p-3 rounded-lg border',
                    zone.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{zone.name}</span>
                    <span className={clsx('text-xs', zone.active ? 'text-green-600' : 'text-gray-400')}>
                      {zone.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {zone.description && (
                    <p className="text-sm text-gray-500 mt-1">{zone.description}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No zones configured</p>
          )}
        </div>

        {/* Save */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={clsx(
              'btn-primary flex items-center gap-2',
              (!hasChanges || saving) && 'opacity-50 cursor-not-allowed'
            )}
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {isAdmin && (
        <>
          <AddUserModal
            isOpen={showAddUser}
            onClose={() => setShowAddUser(false)}
            onCreated={() => refetchUsers()}
          />
          <ResetPasswordModal
            user={resetPasswordUser}
            onClose={() => setResetPasswordUser(null)}
            onReset={() => refetchUsers()}
          />
        </>
      )}
    </div>
  );
}
